import type {
  ThemeFieldPreview,
  ThemePreviewSnapshot,
} from "containers/theme-setter/types";

type AssetModuleContext = {
  keys(): string[];
  <T>(id: string): T;
};

const buildPreviewManifest = (): Record<string, string> => {
  if (typeof require === "undefined") {
    return {};
  }

  let context: AssetModuleContext | undefined;
  try {
    // @ts-expect-error - webpack injects require.context
    context = require.context(
      "assets/previews",
      false,
      /\.(png|jpe?g|svg|gif|webp|avif)$/i
    );
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.warn("[theme-setter] Unable to load preview assets", error);
    }
    return {};
  }

  if (!context) {
    return {};
  }

  const assetContext = context;
  return assetContext.keys().reduce<Record<string, string>>((acc, key) => {
    const normalizedKey = key.replace(/^\.\//, "");
    acc[normalizedKey] = assetContext(key) as string;
    return acc;
  }, {});
};

const previewManifest = buildPreviewManifest();
const DEFAULT_PREVIEW_ASSET = "default-preview.svg";
const loggedMissingAssets = new Set<string>();

const warnMissingAsset = (assetName: string) => {
  if (loggedMissingAssets.has(assetName)) return;
  loggedMissingAssets.add(assetName);
  if (process.env.NODE_ENV === "development") {
    // eslint-disable-next-line no-console
    console.warn(`[theme-setter] Preview asset not found: ${assetName}`);
  }
};

const resolvePreviewAsset = (assetName?: string): string => {
  if (assetName) {
    const normalized = assetName.replace(/^\.\//, "");
    const asset = previewManifest[normalized];
    if (asset) {
      return asset;
    }
    warnMissingAsset(normalized);
  }

  const fallback = previewManifest[DEFAULT_PREVIEW_ASSET];
  if (!fallback) {
    warnMissingAsset(DEFAULT_PREVIEW_ASSET);
  }
  return fallback ?? "";
};

const buildSnapshotFromAsset = (
  assetName: string | undefined,
  alt: string,
  caption?: string
): ThemePreviewSnapshot => ({
  src: resolvePreviewAsset(assetName),
  alt,
  caption,
});

const buildSnapshotsFromAssets = (
  assetNames: string[],
  altPrefix: string,
  captionPrefix: string
): ThemePreviewSnapshot[] =>
  assetNames.map((assetName, index) =>
    buildSnapshotFromAsset(
      assetName,
      assetNames.length === 1
        ? altPrefix
        : `${altPrefix} ${index + 1}`,
      assetNames.length === 1
        ? captionPrefix
        : `${captionPrefix} ${index + 1}`
    )
  );

const buildDefaultPreview = (
  label: string,
  section: string,
  description?: string
): ThemeFieldPreview => ({
  title: label,
  description:
    description ??
    `Static preview for the "${label}" setting inside ${section}. `,
  snapshots: [
    buildSnapshotFromAsset(undefined, `${label} preview`, `${section} preview`),
  ],
});

const buildTextVariantPreview = (
  title: string,
  assetNames: string | string[],
  description: string
): ThemeFieldPreview => {
  const normalized = Array.isArray(assetNames) ? assetNames : [assetNames];
  return {
    title,
    description,
    snapshots: normalized.map((assetName, index) =>
      buildSnapshotFromAsset(
        assetName,
        normalized.length === 1
          ? `${title} text variant preview`
          : `${title} text variant preview ${index + 1}`,
        normalized.length === 1
          ? "Text variant preview"
          : `Text variant preview ${index + 1}`
      )
    ),
  };
};

const landingPreviewOverrides: Record<string, ThemeFieldPreview | undefined> = {
  "landing.metadata.faviconHref": {
    title: "Favicon URL",
    description:
      "Static preview for the 'Favicon URL' setting inside Landing. ",
    snapshots: [
      buildSnapshotFromAsset(
        "favicon.avif",
        "Favicon preview",
        "Favicon styling"
      ),
    ],
  },
  "landing.heroBackground": {
    title: "Hero background",
    description:
      "Static preview for the 'Hero background' setting inside Landing. ",
    snapshots: [
      buildSnapshotFromAsset(
        "heroBackground.avif",
        "Hero background preview",
        "Hero background styling"
      ),
    ],
  },
  "landing.heroRightImage": {
    title: "Hero right image",
    description:
      "Static preview for the 'Hero right image' setting inside Landing. ",
    snapshots: [
      buildSnapshotFromAsset(
        "heroRightImage.avif",
        "Hero right image preview",
        "Hero right image styling"
      ),
    ],
  },
  "landing.metadata.heroPrimaryShapeText": {
    title: "Hero primary shape text",
    description:
      "Static preview for the 'Hero primary text' setting inside Landing. ",
    snapshots: [
      buildSnapshotFromAsset(
        "heroPrimaryText.avif",
        "Hero primary shape text preview",
        "Hero primary shape text styling"
      ),
    ],
  },
  "landing.logos.main.src": {
    title: "Main logo src",
    description:
      "Static preview for the 'Main logo src' setting inside Landing. ",
    snapshots: [
      buildSnapshotFromAsset(
        "mainLogo.avif",
        "Main logo src preview",
        "Main logo src styling"
      ),
    ],
  },
  "landing.metadata.pageTitle": {
    title: "Page title",
    description: "Static preview for the 'Page title' setting inside Landing. ",
    snapshots: [
      buildSnapshotFromAsset(
        "pageTitle.avif",
        "Page title preview",
        "Page title styling"
      ),
    ],
  },
  "landing.logos.simple.src": {
    title: "Simple logo src",
    description:
      "Static preview for the 'Simple logo src' setting inside Landing. ",
    snapshots: [
      buildSnapshotFromAsset(
        "simpleLogo.avif",
        "Simple logo src preview",
        "Simple logo src styling"
      ),
    ],
  },
};

const componentPreviewOverrides: Record<string, ThemeFieldPreview | undefined> =
  {
    sportTabsButtonHeightClass: {
      title: "Sport tabs height",
      description:
        "Controls the Tailwind height class applied to each sport tab button.",
      snapshots: [
        buildSnapshotFromAsset(
          "sportTabsButtonHeightClass.avif",
          "Sport tabs height preview",
          "Height utility example"
        ),
      ],
    },
    sportTabsButtonActiveColorClass: {
      title: "Active sport tab styles",
      description:
        "Defines the background, text, and border utilities for the active sport tab state.",
      snapshots: [
        buildSnapshotFromAsset(
          "sportTabsButtonActiveColorClass.avif",
          "Active tab preview",
          "Active tab styling"
        ),
      ],
    },
    sportTabsButtonInactiveColorClass: {
      title: "Inactive sport tab styles",
      description:
        "Defines the base colors for sport tabs when they are not selected.",
      snapshots: [
        buildSnapshotFromAsset(
          "sportTabsButtonInactiveColorClass.avif",
          "Inactive tab preview",
          "Inactive tab styling"
        ),
      ],
    },
    categoryTabsActiveClass: {
      title: "Active category tab styles",
      description:
        "Tailwind utilities that highlight the selected tab inside CategoryTabs.",
      snapshots: buildSnapshotsFromAssets(
        [
          "categoryTabsActiveClass.avif",
          "categoryTabsActiveClass2.avif",
          "categoryTabsActiveClass3.avif",
        ],
        "Active category tab preview",
        "Category tab preview"
      ),
    },
    categoryTabsInactiveClass: {
      title: "Inactive category tab styles",
      description:
        "Tailwind utilities for CategoryTabs buttons when they are not active.",
      snapshots: buildSnapshotsFromAssets(
        [
          "categoryTabsInactiveClass.avif",
          "categoryTabsInactiveClass2.avif",
          "categoryTabsInactiveClass3.avif",
        ],
        "Inactive category tab preview",
        "Category tab preview"
      ),
    },
    categorySubtypesActiveClass: {
      title: "Active chalkboard subtype styles",
      description:
        "Controls the highlighted pill appearance for selected chalkboard subtypes.",
      snapshots: buildSnapshotsFromAssets(
        [
          "categorySubtypesActiveClass.avif",
          "categorySubtypesActiveClass2.avif",
          "categorySubtypesActiveClass3.avif",
        ],
        "Active subtype preview",
        "Subtype tab preview"
      ),
    },
    categorySubtypesInactiveClass: {
      title: "Inactive chalkboard subtype styles",
      description:
        "Base appearance for unselected chalkboard subtype pills.",
      snapshots: buildSnapshotsFromAssets(
        [
          "categorySubtypesInactiveClass.avif",
          "categorySubtypesInactiveClass2.avif",
          "categorySubtypesInactiveClass3.avif",
        ],
        "Inactive subtype preview",
        "Subtype tab preview"
      ),
    },
  };

const textVariantPreviewOverrides: Record<
  string,
  ThemeFieldPreview | undefined
> = {
  sportTabLabel: buildTextVariantPreview(
    "Sport tab label",
    "sportTabLabel.avif",
    "Typography used for each sport tab row label."
  ),
  categoryTabLabel: buildTextVariantPreview(
    "Category tab label",
    [
      "categoryTabLabel.avif",
      "categoryTabLabel2.avif",
      "categoryTabLabel3.avif",
    ],
    "Typography used for tab labels inside components such as the pass map filters."
  ),
  categorySubtypeLabel: buildTextVariantPreview(
    "Chalkboard subtype label",
    "categorySubtypeLabel.avif",
    "Controls the text styling within chalkboard subtype chips."
  ),
  chartTitle: buildTextVariantPreview(
    "Chart title",
    "chartTitle.avif",
    "Headline text that describes the stat chart."
  ),
  chartValue: buildTextVariantPreview(
    "Chart value",
    "chartValue.avif",
    "Numerical value shown inside charts."
  ),
  playerNameBall: buildTextVariantPreview(
    "Player name ball",
    "playerNameBall.avif",
    "Name label inside the pitch ball overlay."
  ),
  statusMessageTitle: buildTextVariantPreview(
    "Status message title",
    ["statusMessageTitle.avif", "statusMessageTitle2.avif"],
    "Smaller status headline used in notification badges."
  ),
  statusMessageBigTitle: buildTextVariantPreview(
    "Status message big title",
    "statusMessageBigTitle.avif",
    "Hero-style status highlight for breaking plays."
  ),
  timelineEventTitle: buildTextVariantPreview(
    "Timeline event title",
    "timelineEventTitle.avif",
    "Title text for each item inside the match timeline."
  ),
  timelineEventDescription: buildTextVariantPreview(
    "Timeline event description",
    "timelineEventDescription.avif",
    "Supporting copy beneath each timeline event."
  ),
  timelineTitle: buildTextVariantPreview(
    "Timeline title",
    "timelineTitle.avif",
    "Section heading shown at the top of the timeline."
  ),
  timelineMinutePill: buildTextVariantPreview(
    "Timeline minute pill",
    "timelineMinutePill.avif",
    "The circular minute indicator beside each event."
  ),
  prematchInfoText: buildTextVariantPreview(
    "Prematch info text",
    "prematchInfoText.avif",
    "General information block in the pre-match card."
  ),
  prematchKickoffText: buildTextVariantPreview(
    "Prematch kickoff text",
    "prematchKickoffText.avif",
    "Kickoff time label shown before matches."
  ),
  prematchTeamName: buildTextVariantPreview(
    "Prematch team name",
    "prematchTeamName.avif",
    "Team name typography within the pre-match layout."
  ),
  prematchVsText: buildTextVariantPreview(
    "Prematch vs text",
    "prematchVsText.avif",
    "`VS` separator between the competing teams."
  ),
  scoreboardText: buildTextVariantPreview(
    "Scoreboard text",
    ["scoreboardText.avif", "scoreboardText2.avif", "scoreboardText3.avif"],
    "Primary font for the soccer scoreboard title and value."
  ),
  scoreboardBaseballInning: buildTextVariantPreview(
    "Scoreboard baseball inning",
    "scoreboardBaseballInning.avif",
    "Inning label displayed on baseball scoreboards."
  ),
  scoreboardBaseballCounts: buildTextVariantPreview(
    "Scoreboard baseball counts",
    "scoreboardBaseballCounts.avif",
    "Balls/strikes/outs counts typography."
  ),
  iceHockeyTimer: buildTextVariantPreview(
    "Ice hockey timer",
    "iceHockeyTimer.avif",
    "Timer digits inside the ice hockey layout."
  ),
  tennisSets: buildTextVariantPreview(
    "Tennis sets",
    "tennisSets.avif",
    "Set count typography used in tennis scoreboards."
  ),
  basketballTimer: buildTextVariantPreview(
    "Basketball timer",
    "basketballTimer.avif",
    "Game clock styling for basketball overlays."
  ),
  basketballFlashBannerTeamName: buildTextVariantPreview(
    "Basketball flash banner team name",
    "basketballFlashBannerTeamName.avif",
    "Team label in the flash banner highlight."
  ),
  basketballFlashBannerPoints: buildTextVariantPreview(
    "Basketball flash banner points",
    "basketballFlashBannerPoints.avif",
    "Large numeric points inside the flash banner."
  ),
  basketballFlashBannerFooterText: buildTextVariantPreview(
    "Basketball flash banner footer text",
    "basketballFlashBannerFooterText.avif",
    "Supporting footer copy for the flash banner."
  ),
  soccerTimer: buildTextVariantPreview(
    "Soccer timer",
    "soccerTimer.avif",
    "Real-time match clock inside soccer overlays."
  ),
  soccerChalkboardPlayerText: buildTextVariantPreview(
    "Soccer chalkboard player text",
    "soccerChalkboardPlayerText.avif",
    "Player labels rendered on the chalkboard."
  ),
  soccerInfoCardTitle: buildTextVariantPreview(
    "Soccer info card title",
    "soccerInfoCardTitle.avif",
    "Headline for soccer-specific info cards."
  ),
  soccerInfoCardDescription: buildTextVariantPreview(
    "Soccer info card description",
    "soccerInfoCardDescription.avif",
    "Description text within info cards."
  ),
  soccerScoreEffectText: buildTextVariantPreview(
    "Soccer score effect text",
    "soccerScoreEffectText.avif",
    "Animated goal/score text overlay."
  ),
  volleyballScoreboard: buildTextVariantPreview(
    "Volleyball scoreboard",
    "volleyballScoreboard.avif",
    "Main volleyball scoreboard font."
  ),
  volleyballSets: buildTextVariantPreview(
    "Volleyball sets",
    "volleyballSets.avif",
    "Set counter styling for volleyball."
  ),
};

export const getLandingFieldPreview = (
  path: string,
  label: string
): ThemeFieldPreview =>
  landingPreviewOverrides[path] ?? buildDefaultPreview(label, "Landing");

export const getComponentPreview = (key: string): ThemeFieldPreview =>
  componentPreviewOverrides[key] ??
  buildDefaultPreview(key, "Component overrides");

export const getTextVariantPreview = (key: string): ThemeFieldPreview =>
  textVariantPreviewOverrides[key] ?? buildDefaultPreview(key, "Text variants");
